import { useQuery } from "@tanstack/react-query";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useLocale } from "../../hooks/useLocale";
import { Link } from "react-router-dom";
import StatsCard from "../../components/cards/statsCard";
import Table, { Row, Data } from "../../components/ui/table/table";

import {
  OnlyForCashier,
  OnlyForHeadCashier,
  OnlyForHeadFinance,
  OnlyForTransporter,
} from "../../components/Roles";

import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";

// Assets
import DriverStatsIcon2 from "../../static/icons/drivers_icon_2.png";
import CardStatsIcon1 from "../../static/icons/card_icon_1.png";
import CardStatsIcon2 from "../../static/icons/card_icon_2.png";
import {
  fetchCardStats,
  fetchGroupPrefund,
  getPaymentReqStats,
} from "../../api/wallet-service";
import { formatAmount, parseRoleName } from "../../utils/utils";
import { ROUTES } from "../../routes";

import { fetchUserCountStats } from "../../api/product-service";
import { fetchCardLoadReqStats } from "../../api/card-service";

const DashboardPage = () => {
  const { localize } = useLocale(["dashboard"]);

  // globals
  const currentUser = useSelector((s) => s.auth.currentUser);
  const inputDate = useSelector((s) => s.global.inputDate);
  const locale = useSelector((s) => s.global.locale);

  const paymentsQuery = useQuery(["dashboard/payments/stats"], () =>
    getPaymentReqStats({
      clientHashId: currentUser.clientHashId,
      userHashId: currentUser.userHashId,
      dateRange: inputDate.date,
    })
  );
  return (
    <>
      <PageWrapper>
        {/* Header */}
        <Section>
          <div className="d-flex justify-content-between">
            <h4>{localize("heading")}</h4>
          </div>
        </Section>

        {/* Stats and Graphs */}
        <PageStatistics />

        {/* Table  */}
        <div className="d-flex justify-content-between align-items-center mt-5">
          <h5 className="text-muted">{localize("table.name")}</h5>
        </div>
        <Table
          loading={paymentsQuery.isLoading}
          cols={localize("table.cols", { returnObjects: true })}
          dataSize={paymentsQuery?.data?.otherLifecycleStatus.length}
        >
          {/* =============== Display Data from API =============*/}
          {paymentsQuery?.data?.otherLifecycleStatus?.map((item, index) => (
            <Row key={`${item.email}-${index}`}>
              {!item.name || !item.productType ? (
                <>
                  <Data format>
                    {`${currentUser.firstName} ${currentUser.lastName}`}
                  </Data>

                  <Data>{parseRoleName(currentUser.productType)}</Data>
                </>
              ) : (
                <>
                  <Data format>{item.name}</Data>
                  <Data>{parseRoleName(item.productType)}</Data>
                </>
              )}

              <Data>{item.Allocated ? item.Allocated : "-"}</Data>
              <Data>{item.Approved ? item.Approved : "-"}</Data>
              <Data>{item.InProcess ? item.InProcess : "-"}</Data>
              <Data />
            </Row>
          ))}
          {/* unallocated */}
          <Row>
            <Data>{"Unallocated"}</Data>
            <Data />
            <Data />
            <Data />
            <Data />
            <Data>
              <Link
                to={ROUTES.REQUESTS.DRIVER_PAYMENT_REQ}
                className={"btn btn-link"}
              >{`${paymentsQuery?.data?.totalUnAllocated} View Requests`}</Link>
            </Data>
          </Row>
          {/* show empty list */}
        </Table>
      </PageWrapper>
    </>
  );
};

const PageStatistics = () => {
  const currentProgram = useSelector((s) => s.global.currentProgram);
  const currentUser = useSelector((s) => s.auth.currentUser);
  const { localize } = useLocale(["dashboard"]);

  const pageStatQuery = useQuery(
    ["cards/stats"],
    () =>
      fetchCardStats({
        userHashId: currentUser.userHashId,
        clientHashId: currentUser.clientHashId,
        programHashId: currentProgram,
        currencyCode: "AED",
      }),
    {
      enabled: !!currentProgram,
    }
  );

  const groupPrefundQuery = useQuery(
    ["dashboard/stats"],
    () =>
      fetchGroupPrefund({
        userHashId: currentUser.userHashId,
        programHashId: currentProgram,
        currencyCode: "AED",
      }),
    {
      enabled: !!currentProgram,
    }
  );

  const userCountQuery = useQuery(["dashboard/user-count"], () =>
    fetchUserCountStats({
      userHashId: currentUser.userHashId,
    })
  );

  const cardLoadQuery = useQuery(["dashboard/card-load"], () =>
    fetchCardLoadReqStats({
      userHashId: currentUser.userHashId,
    })
  );

  return (
    <>
      {pageStatQuery.isLoading ? (
        <>
          <Section>
            <div className="row gy-3">
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Skeleton height={120} />
              </div>
            </div>
          </Section>
        </>
      ) : (
        <>
          <Section>
            <div className="row gy-3">
              <OnlyForTransporter role={currentUser.productType}>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.availablePrefund")}
                    valueLabel={"AED"}
                    value={formatAmount(pageStatQuery?.data?.balance)}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={CardStatsIcon2}
                    text="Total Active Cards"
                    value={pageStatQuery?.data?.totalActiveCardCount}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={DriverStatsIcon2}
                    text={localize("stats.totalActiveDrivers")}
                    value={pageStatQuery?.data?.totalDriverCount}
                  />
                </div>
              </OnlyForTransporter>

              {/* Only For Head Finance */}
              <OnlyForHeadFinance role={currentUser.productType}>
                <div className="col-sm-12 col-md-6 col-lg-3">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.headcahierCount")}
                    value={userCountQuery?.data?.noOfHeadCashier}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-3">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.cashierCount")}
                    value={userCountQuery?.data?.noOfCashier}
                  />
                </div>

                <div className="col-sm-12 col-md-6 col-lg-3">
                  <StatsCard
                    loading={groupPrefundQuery.isLoading}
                    image={CardStatsIcon1}
                    text={localize("stats.teamBalance")}
                    valueLabel={"AED"}
                    value={formatAmount(
                      groupPrefundQuery?.data?.totalPrefundBalanceToHeadCashier
                    )}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-3">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.availablePrefund")}
                    valueLabel={"AED"}
                    value={formatAmount(pageStatQuery?.data?.balance)}
                  />
                </div>
              </OnlyForHeadFinance>

              {/* Head Cashier Stats */}
              <OnlyForHeadCashier role={currentUser.productType}>
                <div className="col-sm-12 col-md-6 col-lg-6">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.availablePrefund")}
                    valueLabel={"AED"}
                    value={formatAmount(pageStatQuery?.data?.balance)}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                  <StatsCard
                    loading={groupPrefundQuery.isLoading}
                    image={CardStatsIcon1}
                    text={localize("stats.teamBalance")}
                    valueLabel={"AED"}
                    value={formatAmount(
                      groupPrefundQuery?.data?.totalPrefundBalanceToHeadCashier
                    )}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-6">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.numberOfCashier")}
                    value={userCountQuery.data?.noOfCashier}
                  />
                </div>

                <div className="col-sm-12 col-md-6 col-lg-6">
                  <StatsCard
                    loading={cardLoadQuery.isLoading}
                    image={CardStatsIcon1}
                    text={localize("stats.cardLoadRequestAmount")}
                    valueLabel={"AED"}
                    value={formatAmount(
                      cardLoadQuery?.data?.totalAmountOfCardLoadRequests
                    )}
                  />
                </div>
              </OnlyForHeadCashier>
              <OnlyForCashier role={currentUser.productType}>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.cardLoadRequestCount")}
                    value={cardLoadQuery?.data?.totalCardLoadRequests}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.cardLoadRequestedAmount")}
                    valueLabel={"AED"}
                    value={formatAmount(
                      cardLoadQuery?.data?.totalAmountOfCardLoadRequests
                    )}
                  />
                </div>
                <div className="col-sm-12 col-md-6 col-lg-4">
                  <StatsCard
                    image={CardStatsIcon1}
                    text={localize("stats.availablePrefund")}
                    valueLabel={"AED"}
                    value={formatAmount(pageStatQuery?.data?.balance)}
                  />
                </div>
              </OnlyForCashier>
            </div>
          </Section>
        </>
      )}
    </>
  );
};

export default DashboardPage;
